import {GetOverwolfXpSubjectEnum, OverwolfApi} from "@devour/client";
import useGetUserOverwolfXP from "@/hooks/useGetUserOverwolfXP";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import Skeleton from "react-loading-skeleton";
import Tooltip from "@/components/Tooltip";
import {BsFillInfoCircleFill, BsFillQuestionCircleFill} from "react-icons/bs";
import {useEffect, useState} from "react";
import {capitalizeFirstLetter} from "@/utils/formatString/capitalizeFirstLetter";
import {IoClose} from "react-icons/io5";
import getConfig from "@/utils/getConfig";
import FrameOneSwitchInput from "@/components/inputs/FrameOneSwitchInput";
import OverwolfGoVipTimeRewards from "@/pages/overwolf/components/OverwolfGoVipTimeRewards";
import useGetOverwolfGameInfo from "@/hooks/useGetOverwolfGameInfo";
import useOverwolfInterop, {overwolfGameInfo} from "@/hooks/useOverwolfInterop";
import {isDev} from "@/utils/isDev";
import {useGetQuestConfigsForUser} from "@/hooks/useGetQuestConfigsForUser";
import OverwolfGoVipQuestRewards from "./OverwolfGoVipQuestRewards";
import {isDesktop} from "react-device-detect";
import useGetOverwolfGameEventsDisabled from "@/hooks/useGetOverwolfGameEventsDisabled";
import { MdWarning } from "react-icons/md";
import OverwolfGameStatusChip from "@/pages/overwolf/components/OverwolfGameStatusChip";
import {useGetQuestProgressionForUser} from "@/hooks/useGetQuestProgressionForUser";
import {useGetGuilds} from "@/hooks/useGetGuilds";
import {useGetGuildProgress} from "@/hooks/useGetGuildProgress";

export enum RewardsView {
    QUESTS,
    TIME_PLAYED,
}

const testGame = {
    gameId: "10798",
    gameTitle: "Rocket League",
    isGameRunning: true,
} as overwolfGameInfo;

export default function OverwolfGoVipRewards() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const { isOnOverwolf } = useOverwolfInterop();
    const owGameInfo = useSelector((store: IStore) => store.metaStore.overwolfGameInfo);
    const gameInfo = !isOnOverwolf && isDev ? testGame : owGameInfo;
    const [rewardsView, setRewardsView] = useState<RewardsView>(RewardsView.QUESTS);
    const [showWeeklyLimitReached, setShowWeeklyLimitReached] = useState(false);
    const [questDisabledAlertDismissed, setQuestDisabledAlertDismissed] = useState(false);
    const { gameEventsDisabled, state } = useGetOverwolfGameEventsDisabled(gameInfo?.gameId);
    const { data: devourGameInfo } = useGetOverwolfGameInfo(gameInfo?.gameId);
    const { data: questConfigs, isLoading: isQuestConfigLoading } = useGetQuestConfigsForUser(fullToken, {gameId: devourGameInfo?.id});
    const { data: questProgressions } = useGetQuestProgressionForUser(fullToken, questConfigs?.map(q => q.id).filter(Boolean) || []);
    const {
        data: timeRewardsData,
        refetch: refetchTimeRewardsData,
        isLoading: isTimeRewardsDataInitialLoading,
    } = useGetUserOverwolfXP(fullToken, GetOverwolfXpSubjectEnum.TIMEREWARD, undefined);

    const {
        data: guildsResponse,
    } = useGetGuilds(
        undefined,
        undefined,
        undefined,
        [devourGameInfo?.id], // Only fetch guilds for the selected game
    );

    const {
        data: guildProgress,
    } = useGetGuildProgress(guildsResponse?.guilds?.[0]?.id, fullToken);

    const shouldShowQuestEventWarning = devourGameInfo?.isEventSupported && gameEventsDisabled;

    useEffect(() => {
        if (!gameInfo) {
            setRewardsView(RewardsView.TIME_PLAYED);
        }
    }, [gameInfo?.gameId]);

    useEffect(() => {
        /*
         * Show the weekly limit reached message if the user has not dismissed it
         * undefined means the user has not reached the limit yet
         */
        if (timeRewardsData?.isWeeklyLimitFeedbackDismissed === false) {
            setShowWeeklyLimitReached(true);
        }
    }, [timeRewardsData?.isWeeklyLimitFeedbackDismissed]);

    function getHeaderDescription() {
        if (devourGameInfo?.isEventSupported || gameInfo) {
            return "Complete quests to boost XP and earn 10 XP for every hour you play!";
        }

        if (!fullToken) {
            return "Earn 10 XP for every hour you play, with a max of 1000 XP each week!";
        }

        return `Earn 10 XP for every hour of gameplay! ${
            isDesktop ? "The more you play, the more XP you collect." : ""
        }`;
    }

    async function dismissWeeklyLimitFeedback() {
        setShowWeeklyLimitReached(false);
        try {
            await new OverwolfApi(getConfig(fullToken)).dismissWeeklyLimitFeedback();
            await refetchTimeRewardsData();
        } catch {}
    }

    const renderSkeleton = () =>
        <div className="overwolf-govip_rewards_time_list">
            {Array.from({ length: 5 }, (_, i) =>
                <div key={i} className="go-vip-ow_time-reward-card overwolf-card">
                    <div>
                        <p>
                            <Skeleton width={150} />
                        </p>
                        <span>
                            <Skeleton width={110} />
                        </span>
                    </div>
                    <span>
                        <Skeleton width={110} />
                    </span>
                </div>)}
        </div>;

    const renderWarningMessage = () => {
        if (state === 2) {
            return "quest tracking may be unavailable at this time.";
        }
        return "quest tracking is temporarily paused. Please check back soon!";
    };

    return (
        <div className="overwolf-govip_rewards">
            <OverwolfGameStatusChip />
            <div className="overwolf-govip_rewards_header">
                <div>
                    <div className="overwolf-govip_rewards_header_top">
                        <h4>Track Rewards</h4>
                        <Tooltip
                            className="overwolf-govip_rewards_header_tooltip"
                            outsideIcon={BsFillQuestionCircleFill}
                            containerElementClassname="overwolf-govip_rewards"
                        >
                            <div className="overwolf-govip_rewards_header_tooltip_content">
                                Earn up to max {timeRewardsData?.weeklyXpLimit ?? 1000} XP each week! Your weekly XP limit resets every Monday at 12:00 AM
                                (EST).
                            </div>
                        </Tooltip>
                    </div>

                    <div className="overwolf-govip_rewards_header_description">{getHeaderDescription()}</div>
                </div>

                {!!gameInfo &&
                    <FrameOneSwitchInput<RewardsView>
                        className="overwolf-govip_rewards_header_switch"
                        name="overwolf-rewards-view-toggle"
                        value={rewardsView}
                        onToggle={setRewardsView}
                        options={[
                            {
                                render: "Quests",
                                value: RewardsView.QUESTS,
                            },
                            {
                                render: "Time Played",
                                value: RewardsView.TIME_PLAYED,
                            },
                        ]}
                    />
                }
            </div>

            {shouldShowQuestEventWarning &&
                rewardsView === RewardsView.QUESTS &&
                !!gameInfo &&
                !questDisabledAlertDismissed &&
                    <div className="overwolf-govip_rewards_quest_disabled">
                        <div className="overwolf-govip_rewards_quest_disabled_left">
                            <MdWarning size="22px" />
                            <div>
                                {capitalizeFirstLetter(gameInfo.gameTitle)} {renderWarningMessage()}
                            </div>
                        </div>
                        <button onClick={() => setQuestDisabledAlertDismissed(true)} className="reset-button">
                            <IoClose size="22px" />
                        </button>
                    </div>
            }

            {rewardsView === RewardsView.TIME_PLAYED && showWeeklyLimitReached &&
                <div className="overwolf-govip_rewards_time_limit-reached">
                    <div className="overwolf-govip_rewards_time_limit-reached_left">
                        <BsFillInfoCircleFill size="22px" />
                        <div>You have reached your weekly XP limit for Time Play! It resets every Monday at 12:01 AM (EST).</div>
                    </div>
                    <button onClick={dismissWeeklyLimitFeedback} className="reset-button">
                        <IoClose size="22px" />
                    </button>
                </div>
            }

            {rewardsView === RewardsView.TIME_PLAYED &&
                <>
                    {isTimeRewardsDataInitialLoading
                        ? renderSkeleton()
                        : <OverwolfGoVipTimeRewards rewards={timeRewardsData?.transactions || []} />
                    }
                </>
            }

            {rewardsView === RewardsView.QUESTS &&
                <>
                    {isQuestConfigLoading
                        ? renderSkeleton()
                        : <OverwolfGoVipQuestRewards
                            gameId={devourGameInfo?.id}
                            questProgressions={questProgressions}
                            questConfigs={questConfigs || []}
                            guildProgress={guildProgress}
                        />
                    }
                </>
            }

            {/* <OWFooter/> */}
        </div>
    );
}
